import { Button, Dropdown, Form, Input, Space } from "antd";
import { ColumnsType } from "antd/es/table";
import { useEffect, useState } from "react";
import ActionThreeDotIcon from "../../../../assets/icons/ActionThreeDotIcon";
import AddIcon from "../../../../assets/icons/AddIcon";
import EditIcon from "../../../../assets/icons/EditIcon";
import SearchIcon from "../../../../assets/icons/SearchIcon";
import SuccessModal from "../../../../components/common/SuccessModal";
import CustomTable from "../../../../components/ui/CustomTable";
import Loader from "../../../../components/ui/Loader";
import {
  useCreateZoneMutation,
  useGetAllZoneQuery,
  useGetSingleZoneQuery,
  useUpdateZoneMutation,
} from "../../../../features/cashCollection/geoSetup/geoSetupApi";
import useError from "../../../../hooks/useError";
import useModal from "../../../../hooks/useModal";
import useTable from "../../../../hooks/useTable";
import SidebarLayout from "../../../../layouts/SidebarLayout";
import {
  GetAllZoneDataParamsType,
  GetAllZoneDataType,
} from "../../../../types/cashCollection/geoSetup";
import { countParPageSerialNumber } from "../../../../utils/number";
import { capitalize } from "../../../../utils/string";
import { addKeyInArray } from "../../../../utils/utils";
import AddEditManageModal from "../Modal/AddEditManageModal";

export default function ZoneList() {
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [form] = Form.useForm();

  const {
    open: openAddModal,
    handleOpen: handleAddOpen,
    handleClose: handleAddClose,
  } = useModal<undefined, "ADD">();

  const {
    open: openSuccessModal,
    handleOpen: handleSuccessOpen,
    handleClose: handleSuccessClose,
  } = useModal<undefined, "SUCCESS">();

  const {
    page,
    limit,
    handlePagination,
    handleLimit,
    filter,
    handleFilter,
    handleSearch,
    keyword,
  } = useTable<GetAllZoneDataParamsType>();

  const { isError, error, data, isLoading } = useGetAllZoneQuery({
    params: {
      zoneName: keyword,
      skip: undefined,
      page,
      limit,
    },
  });

  const {
    data: getSingleZoneData,
    isError: getSingleZoneIsError,
    error: getSingleZoneError,
    isLoading: getSingleZoneIsLoading,
  } = useGetSingleZoneQuery(selectedId || "", {
    skip: !selectedId,
  });

  const [
    createZone,
    {
      isError: createZoneIsError,
      error: createZoneError,
      isLoading: createZoneIsLoading,
      isSuccess: createZoneIsSuccess,
    },
  ] = useCreateZoneMutation();

  const [
    updateZone,
    {
      isError: updateZoneIsError,
      error: updateZoneError,
      isLoading: updateZoneIsLoading,
      isSuccess: updateZoneIsSuccess,
    },
  ] = useUpdateZoneMutation();

  useError(isError, error, "Get all zone fail");
  useError(getSingleZoneIsError, getSingleZoneError, "Get single zone fail");
  useError(createZoneIsError, createZoneError, "Create zone fail");
  useError(updateZoneIsError, updateZoneError, "Update zone fail");

  const onFinish = (values: any) => {
    const newData = {
      ...values,
      active: true,
    };
    if (selectedId) {
      updateZone({
        id: selectedId,
        data: values,
      });
    } else {
      createZone(newData);
    }
  };

  const getModalTitle = () => {
    if (openAddModal.type === "ADD" && selectedId) {
      return "Zone Successfully Updated!";
    }
    if (openAddModal.type === "ADD") {
      return "Zone Successfully Added!";
    }
    return "";
  };

  const handleOkay = () => {
    setSelectedId(null);
    handleAddClose();
    handleSuccessClose();
    form.setFieldsValue({});
    form.resetFields();
  };

  useEffect(() => {
    if (getSingleZoneData?.data && selectedId) {
      form.setFieldsValue({
        zoneName: capitalize(getSingleZoneData?.data?.zoneName),
      });
    }
  }, [getSingleZoneData?.data, selectedId, form]);

  useEffect(() => {
    if (createZoneIsSuccess) {
      handleSuccessOpen({ type: "SUCCESS" });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [createZoneIsSuccess]);

  useEffect(() => {
    if (updateZoneIsSuccess) {
      handleSuccessOpen({ type: "SUCCESS" });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateZoneIsSuccess]);

  const columns: ColumnsType<GetAllZoneDataType> = [
    {
      title: "#SL",
      align: "center",
      width: 60,
      render: (_, __, index) => countParPageSerialNumber(page, limit, index),
    },

    {
      title: <div className="text-nowrap">Zone</div>,
      dataIndex: "zoneName",
      align: "center",
      render: (val) => {
        return capitalize(val) || "N/A";
      },
    },
    // {
    //   title: <div className="text-nowrap">Created at</div>,
    //   dataIndex: "remaining",
    //   align: "center",
    //   render: (val) => {
    //     return val || "N/A";
    //   },
    // },
    {
      title: <div className="whitespace-nowrap ">Status</div>,
      dataIndex: "active",
      align: "center",
      render: (val: boolean) => {
        const status = val ? "Active" : "Inactive";

        const statusClasses: Record<string, string> = {
          Active: "bg-green-100 text-green-700",
          Inactive: "bg-red-100 text-red-700",
        };

        return (
          <span
            className={`px-2 py-1 rounded text-xs font-medium ${statusClasses[status]}`}
          >
            {status}
          </span>
        );
      },
    },
    {
      title: "Actions",
      dataIndex: "id",
      align: "center",
      render: (val: number, record: any) => {
        const items = [
          // {
          //   key: "active",
          //   label: (
          //     <div className="flex justify-between items-center pb-2 border-b border-[#D7DFE9]">
          //       <span className="text-gray-700">Active</span>
          //       <Switch defaultChecked size="small" />
          //     </div>
          //   ),
          // },
          {
            key: "edit",
            label: (
              <div
                className="flex items-center gap-2 justify-start text-slate-700 text-sm font-medium"
                onClick={() => {
                  setSelectedId(val);
                  handleAddOpen({ type: "ADD" });
                }}
              >
                <EditIcon />
                <span>Edit</span>
              </div>
            ),
          },
        ];

        return (
          <div className="flex justify-center items-center gap-2">
            <Dropdown
              menu={{
                items,
                className: "min-w-[170px] !h-[40px] ",
              }}
              trigger={["click"]}
              placement="bottomRight"
              arrow={{ pointAtCenter: true }}
            >
              <Button
                className="action-button disabled:!bg-[#ddd] !border-none"
                size="small"
                icon={<ActionThreeDotIcon />}
              />
            </Dropdown>
          </div>
        );
      },
    },
  ];

  return (
    <>
      <SidebarLayout title="GEO Set-up">
        <CustomTable
          loading={isLoading}
          limit={limit}
          page={page}
          columns={columns}
          total={data?.data?.count}
          dataSource={addKeyInArray(data?.data?.rows)}
          handleLimit={handleLimit}
          handlePagination={handlePagination}
          filterSection={
            <>
              <Input
                suffix={<SearchIcon />}
                placeholder="Search Zone"
                style={{ width: 215 }}
                allowClear
                onChange={handleSearch}
              />

              <Button
                type="primary"
                className="middle-button"
                icon={<AddIcon />}
                onClick={() => handleAddOpen({ type: "ADD" })}
              >
                New Zone
              </Button>
            </>
          }
        />
      </SidebarLayout>

      {openAddModal?.type === "ADD" && (
        <AddEditManageModal
          open={openAddModal?.open}
          handleClose={handleAddClose}
        >
          <div>
            <div className="justify-start text-zinc-700 text-xl font-medium">
              {selectedId ? "Update Zone" : "Add New Zone"}
            </div>

            {getSingleZoneIsLoading ? (
              <Loader />
            ) : (
              <div className="mt-5">
                <Form
                  className="custom-form"
                  layout="vertical"
                  requiredMark="optional"
                  size="large"
                  onFinish={onFinish}
                  form={form}
                >
                  <Form.Item
                    label="Zone Name"
                    name="zoneName"
                    rules={[
                      { required: true, message: "Please enter zone name" },
                    ]}
                  >
                    <Input placeholder="Enter Zone Name" />
                  </Form.Item>

                  <Space className="w-full mt-5" size={12}>
                    <Button
                      className="normal-dark-button modal-button !h-[40px]"
                      onClick={() => handleAddClose()}
                      size="middle"
                    >
                      Cancel
                    </Button>

                    <Button
                      className="modal-button !h-[40px]"
                      type="primary"
                      size="middle"
                      htmlType="submit"
                      loading={createZoneIsLoading || updateZoneIsLoading}
                    >
                      {selectedId ? "Update Zone" : "Add Zone"}
                    </Button>
                  </Space>
                </Form>
              </div>
            )}
          </div>
        </AddEditManageModal>
      )}

      {openSuccessModal.type === "SUCCESS" && (
        <SuccessModal
          open={openSuccessModal?.open}
          handleClose={handleSuccessClose}
          handleOkay={handleOkay}
          data={openSuccessModal?.data}
          title={getModalTitle()}
        />
      )}
    </>
  );
}
